import React, { useCallback, useEffect, useState } from 'react'
import { DI, DIProps } from '../../../../../Core';
import { Card, Button, ChoiceList, InlineStack, Popover, TextField, BlockStack } from '@shopify/polaris';
import { CalendarMinor } from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';

import { isLeapYear } from '../../Utils/Helper';
import { ChoicesI, CustomDateErrI, CustomDateI } from '../../Utils/Interface';
interface props extends DIProps {
    filter: any
    setFilter: (data: any) => void
    selected: string[]
    setSelected: (val: string[]) => void
}

const RenderCreatedAtFilter = (props: props) => {
    const { t } = useTranslation();
    const { filter, setFilter, selected, setSelected } = props;
    const today: Date = new Date(new Date().setHours(0, 0, 0, 0));


    const formatDate = (inputDate: any) => {
        const date = new Date(inputDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }
    const yesterday: string = formatDate(new Date(new Date().setDate(today.getDate() - 1)));

    const [popoverActive, setPopoverActive] = useState<boolean>(false);
    const [customDate, setCustomDate] = useState<CustomDateI>({ start: '', end: '' })
    const [customDateErr, setCustomDateErr] = useState<CustomDateErrI>({ start: false, end: false })

    /**
     * Handles the change in selected values and updates the state
     * @param {string[]} value The array of selected values
     */
    const handleChange = useCallback((value: string[]): void => setSelected(value), []);

    // array of choices or options for created at date filter
    const choices: Array<ChoicesI> = [
        {
            label: t('Select Date'),
            value: 'Select Date',
            period: { since: formatDate(new Date()), until: formatDate(new Date()) },
            disabled: true
        },
        {
            label: t('Today'),
            value: 'today',
            period: { since: formatDate(new Date()), until: formatDate(new Date()) },
        },
        {
            label: t('Last 7 days'),
            value: 'last7',
            period: { since: formatDate(new Date(new Date().setDate(today.getDate() - 7))), until: yesterday },
        },
        {
            label: t('Last 30 days'),
            value: 'last30',
            period: { since: formatDate(new Date(new Date().setDate(today.getDate() - 30))), until: yesterday },
        },
        {
            label: t('Last 90 days'),
            value: 'last90',
            period: { since: formatDate(new Date(new Date().setDate(today.getDate() - 90))), until: yesterday },
        },
        {
            label: t('Last 180 days'),
            value: 'last180',
            period: { since: formatDate(new Date(new Date().setDate(today.getDate() - 180))), until: yesterday },
        },
        {
            label: t('Last 12 months'),
            value: 'last12Mon',
            period: { since: formatDate(new Date(new Date().setDate(today.getDate() - isLeapYear(today.getFullYear())))), until: yesterday },
        },
        {
            label: t('Custom'),
            value: 'custom',
            period: { since: customDate.start, until: customDate.end },
        }
    ]

    // handle changes in the "Order Date" filter and update selected values accordingly
    useEffect(() => {
        let date_filter = filter['Order Date'];
        if (date_filter?.textValue !== "") setSelected([choices.filter((choice: any) => choice?.label === date_filter?.textValue)[0]?.value])
        if (date_filter?.textValue === 'Custom') setCustomDate({ start: date_filter.start, end: date_filter.end })
    }, [filter['Order Date']])

    /**
     * Handles the change in select input values and updates the filter state
     */
    const handleSelectChange = () => {
        let temp: any = { ...filter };
        Object.keys(temp).forEach((key: any) => {
            if (key === 'Order Date') {
                if (selected[0] !== 'custom') {
                    temp[key]['start'] = choices.filter((choice: any) => choice.value === selected[0])[0].period.since
                    temp[key]['end'] = ''
                    temp[key]['textValue'] = choices.filter((choice: any) => choice.value === selected[0])[0].label
                    temp[key]['change'] = true
                    setPopoverActive(!popoverActive)
                }
                else {
                    if (customDate.start === '') setCustomDateErr({ ...customDateErr, start: true })
                    else if (customDate.end === '') setCustomDateErr({ ...customDateErr, end: true })
                    else if (!customDateErr.start && !customDateErr.end) {
                        temp[key]['start'] = customDate.start
                        temp[key]['end'] = customDate.end + 'T23:59:59'
                        temp[key]['textValue'] = choices.filter((choice: any) => choice.value === selected[0])[0].label
                        temp[key]['change'] = true
                        setPopoverActive(!popoverActive)
                    }
                }
            }
        })

        props.di.globalState.set('orders_filter', JSON.stringify(temp), true)
        setFilter({ ...temp })
    }
    return (
        <Popover
            zIndexOverride={9999}
            active={popoverActive}
            autofocusTarget="none"
            preferredAlignment="left"
            preferredPosition="below"
            fluidContent
            sectioned={false}
            fullHeight
            activator={<Button size="slim" icon={CalendarMinor} onClick={() => setPopoverActive(!popoverActive)}>
                {
                    filter['Order Date']['textValue'] === "" ?
                        choices?.filter((choice: any) => choice.value === selected[0])[0].label
                        : choices?.filter((choice: any) => choice.label === filter['Order Date']['textValue'])[0].label
                }
            </Button>
            }
            onClose={() => setPopoverActive(false)}
        >
            <div style={{ minWidth: '250px' }}>
                <Popover.Pane>
                    <Card roundedAbove='xs'>
                        <BlockStack gap="100">
                            <ChoiceList title="Created At" titleHidden choices={choices} selected={selected} onChange={handleChange} />
                            {
                                selected[0] === 'custom' && (
                                    <React.Fragment>
                                        <TextField
                                            type='date'
                                            label={t("Starting")}
                                            value={customDate.start}
                                            onChange={(value: string) => {
                                                if (value > formatDate(today)) setCustomDateErr({ ...customDateErr, start: true })
                                                else setCustomDateErr({ ...customDateErr, start: false })
                                                setCustomDate({ ...customDate, start: value })
                                            }}
                                            autoComplete="off"
                                            max={formatDate(today)}
                                            error={customDateErr.start}
                                        />
                                        <TextField
                                            type='date'
                                            label={t("Ending")}
                                            value={customDate.end}
                                            onChange={(value: string) => {
                                                if (value !== '' && (value > formatDate(today) || value < "1900-01-01" || value < customDate.start)) setCustomDateErr({ ...customDateErr, end: true })
                                                else setCustomDateErr({ ...customDateErr, end: false })
                                                setCustomDate({ ...customDate, end: value })
                                            }}
                                            autoComplete="off"
                                            max={formatDate(today)}
                                            min={customDate?.start}
                                            error={customDateErr.end}
                                        />
                                    </React.Fragment>
                                )
                            }
                        </BlockStack>
                    </Card>
                </Popover.Pane>

                <Popover.Pane sectioned>
                    <InlineStack align='end' gap="200">
                        <Button onClick={() => {
                            setSelected(['Select Date'])
                            setPopoverActive(!popoverActive)
                        }}>Cancel</Button>
                        <Button onClick={() => handleSelectChange()} variant="primary">{t("Apply")}</Button>
                    </InlineStack >
                </Popover.Pane >
            </div >
        </Popover >
    );
}

export default DI(RenderCreatedAtFilter)